import { isArray, selectMany } from './ArrayHelper';
import { ValidationErrors } from './ErrorHelper';

type ValidationError = {
    item: string;
    error: string;
};

export type ValidationFunc<T> = (val: T) => ValidationError[];

function prefixItem(prefix: string, item?: string) {
    if (!item) return prefix;
    if (item[0] === '[') return `${prefix}${item}`;
    return `${prefix}.${item}`;
}

export function all<T>(...validators: ValidationFunc<T>[]) {
    return (val: T) => validators.map((v) => v(val)).reduce((p, c) => p.concat(c), []);
}

export function field<T, V extends keyof T>(name: V, ...validators: ValidationFunc<T[V] | undefined | null>[]) {
    const validator = all(...validators);
    return (val: T | undefined | null) => {
        const field = val === undefined ? undefined : val === null ? null : val[name];
        return validator(field).map((e) => ({ item: prefixItem(String(name), e.item), error: e.error }));
    };
}

export function eachItem<T>(validator: ValidationFunc<T | undefined | null>): ValidationFunc<T[] | undefined | null> {
    return (val: T[] | undefined | null) =>
        !isArray(val)
            ? []
            : selectMany(
                  val.map((v, i) => validator(v).map((e) => ({ item: prefixItem(`[${i}]`, e.item), error: e.error })))
              );
}

function check<T>(pred: (v: T) => boolean, message: string): ValidationFunc<T | null | undefined> {
    return (val: T | null | undefined) => (nullOrUndefined(val) || pred(val) ? [] : [{ item: '', error: message }]);
}

function isString(v: unknown): v is string {
    return typeof v === 'string';
}

function nullOrUndefined<T>(v: T | undefined | null): v is undefined | null {
    return v === undefined || v === null;
}

function isArrayOrString(v: unknown): v is unknown[] | string {
    return isString(v) || isArray(v);
}

export const required =
    <T>(message = 'Required') =>
    (val: T) =>
        !nullOrUndefined(val) ? [] : [{ item: '', error: message }];

export const requiredNotEmpty =
    (message = 'Required') =>
    (val: string | null | undefined) =>
        !nullOrUndefined(val) && val.trim() !== '' ? [] : [{ item: '', error: message }];

/** Requires a number, rejecting the NaN a cleared number input stores (`FormStore.numberInputProps`). */
export const requiredNumber =
    (message = 'Required') =>
    (val: number | null | undefined) =>
        typeof val === 'number' && !Number.isNaN(val) ? [] : [{ item: '', error: message }];

export const number = (message = 'Must be a number') => check((v) => typeof v === 'number', message);
export const min = (minVal: number, message?: string) =>
    check((v) => typeof v === 'number' && v >= minVal, message ?? `Must be at least ${minVal}`);
export const max = (maxVal: number, message?: string) =>
    check((v) => typeof v === 'number' && v <= maxVal, message ?? `Can't be above ${maxVal}`);
export const minLength = (minLength: number, message?: string) =>
    check((v) => isArrayOrString(v) && v.length >= minLength, message ?? `Must have length of at least ${minLength}`);
export const maxLength = (maxLength: number, message?: string) =>
    check((v) => isArrayOrString(v) && v.length <= maxLength, message ?? `Can't be longer than ${maxLength}`);
export const oneOf = <T = string>(values: (T | undefined)[], message = 'Invalid value') =>
    check<T | undefined>((v) => values.indexOf(v) >= 0, message);
export const anArray = (message = 'Must be an array') => check((v) => isArray(v), message);

export function validate<T>(obj: T, ...validators: ValidationFunc<T>[]): ValidationErrors | undefined {
    const results = all(...validators)(obj);
    if (results.length === 0) return undefined;

    const validationErrors: ValidationErrors = {};
    for (const err of results) {
        const item = err.item;
        if (!validationErrors[item]) validationErrors[item] = [];
        validationErrors[item].push(err.error);
    }
    return validationErrors;
}

const ipv4Pattern = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;

export function isValidIpv4(address: string): boolean {
    const match = ipv4Pattern.exec(address);
    if (!match) return false;
    return match.slice(1).every((octet) => {
        const n = parseInt(octet, 10);
        return n >= 0 && n <= 255 && String(n) === octet;
    });
}

const cidrPattern = /^([^/]+)\/(\d{1,2})$/;

/**
 * Syntactic check only: confirms the value parses as `<ipv4>/<prefix>` with the
 * prefix in [0, 32] and no leading zeros. Does NOT enforce server-side rules
 * such as prefix range 8-28, network-address (host bits zero), or RFC1918 — the
 * server (see Domain/Validators/ValidateCidrBlockAttribute.cs) remains the
 * source of truth for those. This client check exists to short-circuit obvious
 * typos before hitting the API, not to mirror server validation.
 */
export function isValidCidr(cidr: string): boolean {
    const match = cidrPattern.exec(cidr);
    if (!match) return false;
    const [, address, prefixStr] = match;
    if (!address || !prefixStr) return false;
    if (!isValidIpv4(address)) return false;
    const prefix = parseInt(prefixStr, 10);
    return prefix >= 0 && prefix <= 32 && String(prefix) === prefixStr;
}

export const is = {
    required,
    requiredNotEmpty,
    requiredNumber,
    number,
    min,
    max,
    minLength,
    maxLength,
    oneOf,
    validIf: check,
    invalidIf: <T>(p: (v: T) => boolean, message: string) => check<T>((v) => !p(v), message),
    array: anArray,
};
